/**
 * useGamificationStore — XP, levels, streaks, achievements, coins, rewards shop.
 *
 * Storage: 'gamification' (single doc) — LOCAL_ONLY in storage.js's sync policy
 * (see src/utils/storage.js LOCAL_ONLY_SINGLE_DOC_KEYS). Direct client writes to
 * users/{uid}/_singles/gamification are forgeable and firestore.rules denies them
 * on purpose; storage.set(STORAGE_KEY, ...) below only ever reaches localStorage.
 * The durable, server-authoritative path is syncSessionAward() at the bottom of
 * this file, which calls the awardEvent callable
 * (functions/src/gamification/awardEvent.js) and reconciles its response back in.
 * It is BUILT AND TESTED BUT DELIBERATELY OFF — see SERVER_AWARDS_ENABLED below.
 *
 * Methods: addXP, updateStreak, checkAchievements, setWeeklyGoal,
 * grantCircuitCompletion, addFocusMinutesToWeeklyGoal, buyItem, equipItem,
 * resetProfile, popXPToast, dismissLevelUp, popToast, syncSessionAward.
 * Cross-store: called by src/utils/sessionHelper.js after session completion, and
 * by useCircuitRunStore after a Circuit finishes (grantCircuitCompletion).
 */
import { create } from 'zustand';
import { httpsCallable } from 'firebase/functions';
import { storage, onRehydrate } from '../utils/storage';
import { functions } from '../firebase/config';
import { LEVEL_THRESHOLDS, LEVEL_NAMES, ACHIEVEMENTS, CIRCUIT_COMPLETION_BONUS } from '../utils/constants';
import { localDateStr } from '../utils/dateUtils';

const STORAGE_KEY = 'gamification';

/**
 * Server-authoritative awards: BUILT, TESTED, AND DELIBERATELY OFF (2026-08-30).
 *
 * syncSessionAward() and the awardEvent callable behind it are complete and covered.
 * They stay disabled because the server ledger models only four fields — totalXP,
 * coins, currentStreak, unlockedAchievements — while this profile also carries
 * inventory, equippedTheme, weeklyGoal and awardedCircuitRuns, which the server has
 * no concept of. Two consequences follow, and the second is why this is a flag and
 * not a shipped feature:
 *
 *   1. A fresh device can restore XP and streak but NOT purchases or equipped items,
 *      so a "restored" account silently comes back poorer than it was.
 *   2. Worse: the shipped desktop build (v0.1.9, months behind main) still treats
 *      'gamification' as a cloud-synced key and does an UNCONDITIONAL pull-overwrite
 *      of localStorage on login — see v0.1.9 src/utils/storage.js:450. The moment a
 *      server document exists, that old client overwrites its complete local profile
 *      with the server's partial one and the missing fields are gone. No change in
 *      this repo can fix an already-shipped client.
 *
 * BEFORE FLIPPING THIS ON, all three must be true:
 *   a. The server ledger covers the WHOLE profile, not a subset of it.
 *   b. A desktop build containing the current storage.js sync policy has actually
 *      shipped and users have had time to update.
 *   c. XP inflation is acceptable, or closed — a client seeds its own baseline, which
 *      is fine while XP is self-motivational and buys only cosmetics, and is not fine
 *      once Weekly Leaderboards make it competitive.
 *
 * Until then every award stays local-first, exactly as it has behaved for months.
 * Tracked in docs/roadmaps/engineering.md.
 *
 * Exported as a mutable holder rather than a bare const so the tests that prove the
 * path works can switch it on deliberately — the capability stays fully covered while
 * the shipped default stays off. Production code never writes to it.
 */
export const serverAwards = { enabled: false };

// Lazy singleton, mirroring src/utils/circuitProxy.js — constructed on first actual
// use so importing this store never touches Firebase Functions by itself (tests that
// never call syncSessionAward never need to mock it).
let _awardEventFn = null;
function getAwardEventFn() {
    if (!_awardEventFn) _awardEventFn = httpsCallable(functions, 'awardEvent', { timeout: 15000 });
    return _awardEventFn;
}

function getLevelFromXP(xp) {
    for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
        if (xp >= LEVEL_THRESHOLDS[i]) return i + 1;
    }
    return 1;
}

/**
 * Fields the callable's response must carry as finite numbers to be considered well-formed
 * (see isWellFormedAwardResult below). `coins` stays in this shape check even though the
 * reconcile below no longer merges it into the local profile (FINDING 3, 2026-08-30) — the
 * server still always returns it, and a response missing/mangling it is still a sign of a bug
 * worth rejecting outright rather than partially trusting.
 */
const AWARD_RESULT_NUMERIC_FIELDS = ['totalXP', 'coins', 'currentStreak'];

/**
 * A callable can REJECT (network/App Check/quota — the try/catch in syncSessionAward already
 * covers that) or it can RESOLVE with a malformed/partial payload: a server bug, a stale client
 * expecting fields a later deploy renamed, a misbehaving mock in a test. Only the second case
 * needs an explicit guard here — without it, the reconcile below would happily merge
 * `undefined`/NaN straight into totalXP/coins/currentStreak. `unlockedAchievements` is checked
 * too since the merge spreads it; everything else on `result` (applied, xpAwarded, level, ...)
 * is only ever echoed back to the caller, never written into the profile, so it isn't gated here.
 */
function isWellFormedAwardResult(result) {
    if (!result || typeof result !== 'object') return false;
    if (!AWARD_RESULT_NUMERIC_FIELDS.every((k) => Number.isFinite(result[k]))) return false;
    if (result.unlockedAchievements !== undefined && !Array.isArray(result.unlockedAchievements)) return false;
    return true;
}

/**
 * MONOTONIC MERGE: the server is authoritative for growth, never for shrinkage. Takes the
 * higher of the local and server values for one field, exactly like longestStreak has always
 * been protected (`Math.max(local, server-derived)`, below). Warns instead of throwing or
 * silently applying when the server actually replies lower than local — that is always a bug
 * to chase (a still-seeding baseline, a stale doc read, a clock race), never a legitimate
 * reason to take XP/coins/streak away from someone (AGENTS.md: guilt-free, never punitive).
 */
function higherOrWarn(field, localValue, serverValue) {
    const local = Number.isFinite(localValue) ? localValue : 0;
    const server = Number.isFinite(serverValue) ? serverValue : local;
    if (server < local) {
        console.warn(`[Gamification] awardEvent returned a lower ${field} than the local value (server ${server} < local ${local}) — keeping the local value.`);
        return local;
    }
    return server;
}

/**
 * Local Monday-based week key ('YYYY-MM-DD' of that week's Monday) for a date/ISO string.
 * Used to detect week rollover for the guilt-free weekly focus-minutes goal reset.
 */
function weekKeyOf(dateInput) {
    const d = dateInput ? new Date(dateInput) : new Date();
    if (isNaN(d.getTime())) return localDateStr(new Date());
    const day = d.getDay();                 // 0=Sun..6=Sat
    const offset = (day + 6) % 7;           // days since Monday
    const monday = new Date(d);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(d.getDate() - offset);
    return localDateStr(monday);
}

function getDefaultProfile() {
    return {
        totalXP: 0,
        currentStreak: 0,
        longestStreak: 0,
        lastSessionDate: null,
        unlockedAchievements: [],
        coins: 0,
        inventory: [],
        equippedTheme: null,
        // Focus-minutes goal. Canonical unit is MINUTES on both target and current so
        // progress math never needs a 60× conversion (default 1200 min = 20 h / week).
        weeklyGoal: { target: 1200, current: 0, unit: 'minutes', weekStart: null },
        awardedCircuitRuns: [], // idempotency markers for circuit-completion bonuses
    };
}

const useGamificationStore = create((set, get) => ({
    profile: storage.get(STORAGE_KEY, getDefaultProfile()),

    // ─── Level-up & XP toast tracking ───
    previousLevel: getLevelFromXP(storage.get(STORAGE_KEY, getDefaultProfile()).totalXP),
    levelUpData: null,      // { oldLevel, newLevel, name } — set on level-up, consumed by overlay
    xpToastQueue: [],       // [{ amount, timestamp }] — consumed by XPToast

    get level() {
        return getLevelFromXP(get().profile.totalXP);
    },

    getLevel() {
        return getLevelFromXP(get().profile.totalXP);
    },

    getLevelProgress() {
        const xp = get().profile.totalXP;
        const level = getLevelFromXP(xp);
        const currentThreshold = LEVEL_THRESHOLDS[level - 1] || 0;
        const nextThreshold = LEVEL_THRESHOLDS[level] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1] * 1.5;
        const progress = (xp - currentThreshold) / (nextThreshold - currentThreshold);
        return { level, xp, currentThreshold, nextThreshold, progress: Math.min(progress, 1) };
    },

    addXP(amount) {
        const safeAmount = Math.max(0, amount);
        const oldLevel = get().previousLevel;
        set(state => {
            const updated = {
                ...state.profile,
                totalXP: state.profile.totalXP + safeAmount,
                coins: (state.profile.coins || 0) + safeAmount,
            };
            const newLevel = getLevelFromXP(updated.totalXP);
            const didLevelUp = newLevel > oldLevel;
            storage.set(STORAGE_KEY, updated);
            return {
                profile: updated,
                previousLevel: newLevel,
                xpToastQueue: [...state.xpToastQueue, { amount: safeAmount, timestamp: Date.now() }],
                ...(didLevelUp ? {
                    levelUpData: {
                        oldLevel,
                        newLevel,
                        name: LEVEL_NAMES[newLevel - 1] || 'Ascendant',
                    },
                } : {}),
            };
        });
    },

    popXPToast: () => set(state => ({ xpToastQueue: state.xpToastQueue.slice(1) })),
    dismissLevelUp: () => set({ levelUpData: null }),

    updateStreak(sessionDateIso) {
        const sessionDate = localDateStr(sessionDateIso); // normalize to local YYYY-MM-DD
        const { profile } = get();
        const lastDate = profile.lastSessionDate;

        // Already tracked today — no-op
        if (lastDate === sessionDate) return;

        let newStreak = 1;
        if (lastDate) {
            const last = new Date(lastDate + 'T00:00:00');
            const current = new Date(sessionDate + 'T00:00:00');
            const diffDays = Math.round((current - last) / (1000 * 60 * 60 * 24));

            if (diffDays === 1) {
                // Consecutive day — extend streak
                newStreak = (profile.currentStreak || 0) + 1;
            }
            // diffDays > 1 → gap, reset to 1 (already set above)
            // diffDays <= 0 → past date edge case, treat as reset
        }

        const newLongest = Math.max(profile.longestStreak || 0, newStreak);

        set(state => {
            const updated = {
                ...state.profile,
                currentStreak: newStreak,
                longestStreak: newLongest,
                lastSessionDate: sessionDate,
            };
            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });
    },

    // ...
    toastQueue: [],
    
    popToast: () => set(state => ({ toastQueue: state.toastQueue.slice(1) })),

    checkAchievements(stats) {
        const { profile } = get();
        const newUnlocks = [];

        ACHIEVEMENTS.forEach(achievement => {
            if (!profile.unlockedAchievements.includes(achievement.id)) {
                if (achievement.condition(stats)) {
                    newUnlocks.push(achievement.id);
                }
            }
        });

        if (newUnlocks.length > 0) {
            set(state => {
                const updated = {
                    ...state.profile,
                    unlockedAchievements: [...state.profile.unlockedAchievements, ...newUnlocks],
                };
                storage.set(STORAGE_KEY, updated);
                return { 
                    profile: updated,
                    toastQueue: [...(state.toastQueue || []), ...newUnlocks]
                };
            });
        }

        return newUnlocks;
    },

    setWeeklyGoal(target) {
        set(state => {
            const updated = {
                ...state.profile,
                weeklyGoal: { ...state.profile.weeklyGoal, target },
            };
            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });
    },

    /**
     * Idempotent flat bonus for finishing a whole Circuit (all steps). This is an
     * ADDITIONAL reward on top of the per-step session XP already granted by
     * finalizeSession — it never double-counts. Guarded by a persisted awarded-run
     * marker (runKey), so it fires at most once per run across reloads/snapshots.
     *
     * @param {string} runKey - stable per-run id (e.g. `${circuitId}:${startedAt}`)
     * @returns {boolean} true if the bonus was applied, false if a no-op (already awarded / falsy key)
     */
    grantCircuitCompletion(runKey) {
        if (!runKey) return false;
        const awarded = get().profile.awardedCircuitRuns || [];
        if (awarded.includes(runKey)) return false;

        // Record the marker BEFORE awarding so a re-entrant snapshot can't double-fire.
        set(state => {
            const updated = {
                ...state.profile,
                awardedCircuitRuns: [...(state.profile.awardedCircuitRuns || []), runKey],
            };
            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });

        // Flat bonus via the shared XP path (level-up + toast + coins handled there).
        get().addXP(CIRCUIT_COMPLETION_BONUS);

        // Focus-shaped achievement for finishing Circuits.
        get().checkAchievements({
            totalCircuitsCompleted: (get().profile.awardedCircuitRuns || []).length,
        });

        return true;
    },

    /**
     * Guilt-free weekly focus-minutes goal. Increments weeklyGoal.current by the
     * session's actual focus minutes; at the start of a new week, RESETS current to
     * this session's minutes (never penalizes a missed week — just starts fresh).
     *
     * @param {number} minutes - actual focus minutes from the completed session
     * @param {string} [nowIso] - ISO/date string for the session (defaults to now); used for the week boundary
     */
    addFocusMinutesToWeeklyGoal(minutes, nowIso) {
        const mins = Number(minutes);
        if (!(mins > 0)) return;
        const wk = weekKeyOf(nowIso);

        set(state => {
            const goal = state.profile.weeklyGoal || { target: 1200, current: 0, unit: 'minutes', weekStart: null };
            const isNewWeek = goal.weekStart !== wk;
            const base = isNewWeek ? 0 : (goal.current || 0);
            const updated = {
                ...state.profile,
                weeklyGoal: { ...goal, current: base + mins, weekStart: wk },
            };
            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });
    },

    /**
     * Session-completion SERVER SYNC (Phase P2 durable path).
     *
     * Call this IN ADDITION TO the existing optimistic local calls
     * (addXP/updateStreak/addFocusMinutesToWeeklyGoal/checkAchievements) that
     * src/utils/sessionHelper.js's finalizeSession() already makes on every session
     * completion — never instead of them, and never awaited by the caller. The
     * numbers the user sees immediately come from that optimistic path; this method
     * only reconciles the authoritative server totals in once the round trip
     * completes, so a slow or failed network call can never stall or scare the UI
     * (AGENTS.md: guilt-free, zero-maintenance — no error toast about XP).
     *
     * Calls the awardEvent callable (functions/src/gamification/awardEvent.js) — the
     * ONLY writer firestore.rules allows for users/{uid}/_singles/gamification (see
     * storage.js's LOCAL_ONLY_SINGLE_DOC_KEYS: 'gamification'). The callable
     * recomputes XP itself from (minutes, completed, its OWN currentStreak) — it does
     * not trust a client-supplied XP number — and is idempotent per (uid, eventId):
     * calling this twice with the same eventId (e.g. a retry after a page reload)
     * applies the award at most once server-side.
     *
     * On success, reconciles the server's authoritative totalXP/currentStreak into the local
     * profile by MONOTONIC MERGE — taking the higher of local and server for each field, never
     * a blind overwrite (see higherOrWarn above) — and unions unlockedAchievements (the server
     * only echoes ones it newly unlocked, not the full list, since it may not have full stats —
     * see `stats` below). `coins` is DELIBERATELY EXCLUDED from this merge (FINDING 3,
     * 2026-08-30) — see the dedicated comment at the merge site for why a monotonic merge that
     * is correct for XP/streak would let this one silently refund coins a user already spent.
     * Deliberately does NOT re-fire toasts/the level-up overlay on reconcile; the optimistic
     * path already handled that UX, so this is a silent correction.
     *
     * Before any of that, the response is shape-checked (isWellFormedAwardResult): a callable
     * that RESOLVES with a malformed/partial payload (rather than rejecting) leaves the local
     * profile completely untouched and logs a warning, exactly like a rejected call below —
     * it never merges undefined/NaN into totalXP/coins/currentStreak.
     *
     * On failure (offline, callable error, unknown eventId race, etc.) this only logs
     * a warning — the optimistic local numbers already shown to the user are left
     * exactly as they are.
     *
     * Wired in by src/utils/sessionHelper.js finalizeSession(), which calls this
     * fire-and-forget (`.catch(() => {})`, never awaited) right after its own optimistic
     * addXP/updateStreak/addFocusMinutesToWeeklyGoal/checkAchievements calls, passing the
     * session's own id as eventId, { totalSessions, totalFocusTime } as stats, and the
     * PRE-session profile snapshot as baseline (see `baseline` below).
     *
     * @param {Object} opts
     * @param {string} opts.eventId - stable idempotency key — pass the session's own id.
     * @param {number} opts.minutes - focus minutes for this session.
     * @param {boolean} opts.completed - whether the session completed naturally.
     * @param {string} [opts.dayLocal] - device-local 'YYYY-MM-DD' for streak bookkeeping;
     *   defaults to today. Always pass this explicitly from the caller when possible —
     *   the callable's own fallback uses UTC, which can land on the wrong day near midnight.
     * @param {Object} [opts.stats] - extra achievement-check fields the server can't
     *   derive on its own (e.g. { totalSessions, totalFocusTime }) — forwarded verbatim
     *   into the callable's payload.stats. Omit to let the server check achievements
     *   against XP/streak/level alone (session-count/focus-time achievements won't be
     *   server-confirmed in that case — they still unlock locally via checkAchievements).
     * @param {Object} [opts.baseline] - this account's current totals ({ totalXP, coins,
     *   currentStreak, longestStreak, lastSessionDate }), forwarded verbatim as the
     *   callable's `baseline` field. The server (functions/src/gamification/awardEvent.js)
     *   uses this to seed a gamification document that does not exist yet — every existing
     *   user's ledger has never been written, so without this every account's first sync
     *   would seed from zero and overwrite months of local history. It is ALSO taken into
     *   account (never just discarded) once a document already exists: the server heals its
     *   ledger upward to the higher of what it holds and what this baseline offers, for
     *   XP/coins/streak — this is what repairs a SECOND device/browser that synced first
     *   with empty local storage and seeded the ledger near zero. Healing can only ever raise
     *   a field, never lower one, so it is always safe to send. Pass the PRE-session profile
     *   (before this session's own optimistic award), never
     *   the post-session one — seeding from a total that already includes this session
     *   would double-count it once the server adds its own award on top. Omit entirely
     *   (rather than pass a zeroed object) when the caller has no meaningful profile to
     *   offer — the callable then seeds from zero, same as before this parameter existed.
     * @returns {Promise<Object|null>} the callable's result data, or null if it failed,
     *   came back malformed, or no eventId was supplied. Intended to be called WITHOUT
     *   awaiting.
     */
    async syncSessionAward({ eventId, minutes, completed, dayLocal, stats, baseline } = {}) {
        if (!serverAwards.enabled) return null;

        if (!eventId) {
            console.warn('[Gamification] syncSessionAward called without an eventId — skipping server sync (optimistic local XP already applied).');
            return null;
        }

        try {
            const payload = {
                minutes: Number(minutes) || 0,
                completed: !!completed,
                dayLocal: dayLocal || localDateStr(new Date()),
            };
            if (stats) payload.stats = stats;

            const callData = { eventId, type: 'session_completed', payload };
            // See `baseline` in the doc above — safe to always forward when supplied; the
            // server ignores it outright once a gamification document already exists.
            if (baseline) callData.baseline = baseline;

            const awardEvent = getAwardEventFn();
            const { data: result } = await awardEvent(callData);

            if (!isWellFormedAwardResult(result)) {
                console.warn('[Gamification] awardEvent returned a malformed response — keeping optimistic local totals:', result);
                return null;
            }

            set(state => {
                const p = state.profile;
                const merged = {
                    ...p,
                    // MONOTONIC MERGE — never let a reconciled field go DOWN (see
                    // higherOrWarn above). This is the fix for the overwrite defect: an
                    // established local profile syncing against a server ledger that was
                    // JUST seeded (or is otherwise behind) must never be clobbered downward.
                    totalXP: higherOrWarn('totalXP', p.totalXP, result.totalXP),
                    // `coins` IS NOT RECONCILED FROM THE SERVER (FINDING 3, 2026-08-30).
                    // Unlike XP/streak, coins are not purely-growing: buyItem() spends them
                    // LOCALLY, and the server ledger has no concept of spending at all — it
                    // only ever grows (applyXp adds coins 1:1 with XP on every award, with no
                    // subtraction path anywhere server-side). A monotonic max-merge that is
                    // correct for XP/streak is therefore WRONG for coins: the server total
                    // only ever goes up, so it eventually exceeds any post-spend local
                    // balance, and a max-merge would then silently "refund" whatever the user
                    // already spent — the mirror image of the overwrite-to-zero bug this same
                    // merge was built to fix. Decision: until the server has its own
                    // spend-tracking, coins stay client-authoritative and are never touched by
                    // this reconcile — not merged, not overwritten, just left as `...p` above
                    // already has them. (The one-way seed/heal direction in awardEvent.js —
                    // client baseline INTO the server ledger — is unaffected and still safe:
                    // it never flows back down to overwrite a client's own post-spend balance.)
                    currentStreak: higherOrWarn('currentStreak', p.currentStreak, result.currentStreak),
                    // The callable doesn't return longestStreak — never let it regress
                    // below the (possibly server-corrected) currentStreak.
                    longestStreak: Math.max(p.longestStreak || 0, result.currentStreak || 0),
                    unlockedAchievements: Array.from(new Set([
                        ...(p.unlockedAchievements || []),
                        ...(result.unlockedAchievements || []),
                    ])),
                };
                // LOCAL_ONLY key — this only ever reaches localStorage (see the file
                // header comment); it mirrors the server doc for instant local reads.
                storage.set(STORAGE_KEY, merged);
                return { profile: merged };
            });

            return result;
        } catch (err) {
            console.warn('[Gamification] awardEvent sync failed — keeping optimistic local totals:', err.message);
            return null;
        }
    },

    // Economy
    buyItem(itemId, cost) {
        const { profile } = get();
        if (profile.coins < cost) return false;
        if (profile.inventory.includes(itemId)) return true; // Already owned

        set(state => {
            const updated = {
                ...state.profile,
                coins: state.profile.coins - cost,
                inventory: [...state.profile.inventory, itemId],
            };
            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });
        return true;
    },

    equipItem(itemId, type) {
        // type: 'theme' | 'avatar'
        set(state => {
            let updated = { ...state.profile };
            if (type === 'theme') updated.equippedTheme = itemId;
            if (type === 'avatar') updated.equippedAvatar = itemId;

            storage.set(STORAGE_KEY, updated);
            return { profile: updated };
        });
    },

    resetProfile() {
        const fresh = getDefaultProfile();
        storage.set(STORAGE_KEY, fresh);
        set({ profile: fresh });
    },
}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useGamificationStore.setState({ profile: storage.get(STORAGE_KEY, getDefaultProfile()) });
});

export default useGamificationStore;
